import { DropdownItem, MultiDropdown } from "@/design-system/Dropdown/Dropdown";
import { Switch } from "@/design-system/Switch/Switch";
import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Children, ReactNode, useEffect, useState } from "react";

// Ports of apps/mobile/components/filters/FilterPrimitives.tsx.

export const FilterCard = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) =>
  // A card whose rows are all hidden draws nothing.
  Children.toArray(children).length === 0 ? null : (
    <div
      className={cn(
        "flex flex-col rounded-2xl border border-edge-strong bg-surface [&>*+*]:border-t [&>*+*]:border-edge-strong",
        className,
      )}
    >
      {children}
    </div>
  );

export const FilterSectionLabel = ({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}) => (
  <div className="flex items-baseline justify-between px-0.5 pb-2">
    <span className="text-xs font-bold uppercase tracking-widest text-foreground-muted">
      {title}
    </span>
    {hint && <span className="text-xs text-foreground-muted">{hint}</span>}
  </div>
);

/** A "Label ... Any >" row that opens a searchable multi-select dropdown. */
export const PickerRow = ({
  label,
  items,
  selected,
  onToggle,
  searchPlaceholder,
  hideSearch,
}: {
  label: string;
  items: DropdownItem[];
  selected: number[];
  onToggle: (id: number) => void;
  searchPlaceholder?: string;
  hideSearch?: boolean;
}) => {
  const { t } = useTranslation("common");
  const first = items.find((i) => i.value === String(selected[0]));
  const summary =
    selected.length === 0
      ? t("any")
      : `${first?.label ?? selected[0]}${selected.length > 1 ? ` +${selected.length - 1}` : ""}`;

  return (
    <MultiDropdown
      className="block w-full"
      width={318}
      align="end"
      items={items}
      label={label}
      selected={selected.map(String)}
      onChange={(item) => onToggle(Number(item.value))}
      searchPlaceholder={searchPlaceholder}
      hideSearch={hideSearch}
      trigger={(open) => (
        <button
          type="button"
          className="flex w-full items-center gap-x-2.5 px-3.5 py-3 text-left transition-colors hover:bg-surface-raised"
        >
          <span className="flex-1 text-sm text-foreground-body">{label}</span>
          {selected.length > 0 && first?.icon}
          <span
            className={cn(
              "max-w-40 truncate text-sm font-semibold",
              selected.length > 0 ? "text-foreground" : "text-foreground-muted",
            )}
          >
            {summary}
          </span>
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-foreground-disabled transition-transform",
              open && "rotate-90",
            )}
          />
        </button>
      )}
    />
  );
};

export const SegmentRow = <T extends number>({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: { id: T; label: ReactNode }[];
  selected: T[];
  onToggle: (id: T) => void;
}) => (
  <div className="flex flex-col gap-y-2 px-3.5 py-3">
    <span className="text-sm text-foreground-body">{label}</span>
    <div className="flex gap-x-1.5">
      {options.map((o) => {
        const isSelected = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            className={cn(
              "h-9 flex-1 rounded-xl border text-sm font-bold transition-colors",
              isSelected
                ? "border-primary-border bg-blue-900/50 text-foreground"
                : "border-edge-strong bg-background text-foreground-muted hover:text-foreground",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

export const NestedToggleRow = ({
  label,
  hint,
  isActive,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  isActive: boolean;
  disabled?: boolean;
  onChange: (active: boolean) => void;
}) => (
  <div className="flex items-center gap-x-3 bg-surface-raised py-2.5 pl-7 pr-3.5">
    <div className="w-0.5 self-stretch rounded-full bg-control" />
    <div className="flex flex-1 flex-col">
      <span className="text-sm text-foreground-body">{label}</span>
      <span className="text-xs text-foreground-muted">{hint}</span>
    </div>
    <Switch
      isActive={isActive}
      disabled={disabled}
      activeColor="bg-primary"
      onChange={onChange}
    />
  </div>
);

export const NumberInput = ({
  value,
  placeholder,
  min,
  max,
  format,
  onChange,
}: {
  value: number | null;
  placeholder: string;
  min: number;
  max: number;
  /** Shows the value in another unit inside the box (height's ft/in beside cm). */
  format?: (v: number) => string;
  onChange: (v: number | null) => void;
}) => {
  const [local, setLocal] = useState(value == null ? "" : String(value));
  const { debouncedSearchTerm } = useDebounce(local);

  useEffect(() => setLocal(value == null ? "" : String(value)), [value]);

  // Typing commits only in-range values; a half-typed one ("8" toward 80
  // with a floor of 75) waits, and is clamped on blur or Enter.
  const commit = (raw: string, clamp: boolean) => {
    if (raw === "") {
      if (value !== null) onChange(null);
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    const inRange = n >= min && n <= max;
    if (!inRange && !clamp) return;
    const next = Math.min(max, Math.max(min, n));
    if (next !== value) onChange(next);
    if (next !== n) setLocal(String(next));
  };

  useEffect(() => {
    commit(debouncedSearchTerm, false);
  }, [debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const input = (className: string) => (
    <input
      inputMode="numeric"
      value={local}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value.replace(/\D/g, ""))}
      onBlur={() => commit(local, true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit(local, true);
      }}
      className={cn(
        "text-sm font-medium text-foreground-body placeholder:text-foreground-muted focus:outline-none",
        className,
      )}
    />
  );

  if (!format)
    return input(
      "h-9 w-16 rounded-md bg-background text-center focus:ring-1 focus:ring-primary-border",
    );

  // An empty box converts its placeholder bound, dimmed, so it never reads half empty.
  const shown = value ?? Number(placeholder);
  return (
    <label className="flex h-9 w-24 cursor-text items-center justify-center gap-x-1 rounded-md bg-background focus-within:ring-1 focus-within:ring-primary-border">
      {input("w-8 bg-transparent text-right")}
      {!Number.isNaN(shown) && (
        <span
          className={cn(
            "text-xs",
            value == null
              ? "text-foreground-disabled"
              : "text-foreground-muted",
          )}
        >
          {format(shown)}
        </span>
      )}
    </label>
  );
};

export const RangeInputs = ({
  minValue,
  maxValue,
  range,
  maxPlaceholder,
  format,
  onChangeMin,
  onChangeMax,
}: {
  minValue: number | null;
  maxValue: number | null;
  range: [number, number];
  maxPlaceholder?: string;
  format?: (v: number) => string;
  onChangeMin: (v: number | null) => void;
  onChangeMax: (v: number | null) => void;
}) => (
  <div className="flex items-center gap-x-1.5">
    <NumberInput
      value={minValue}
      placeholder={String(range[0])}
      min={range[0]}
      max={range[1]}
      format={format}
      onChange={onChangeMin}
    />
    <span className="text-sm text-foreground-disabled">–</span>
    <NumberInput
      value={maxValue}
      placeholder={maxPlaceholder ?? String(range[1])}
      min={range[0]}
      max={range[1]}
      format={format}
      onChange={onChangeMax}
    />
  </div>
);

export const ValueRow = ({
  icon,
  label,
  children,
}: {
  icon?: ReactNode;
  label: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex items-center gap-x-2.5 px-3.5 py-2.5">
    {icon && (
      <span className="flex size-5.5 shrink-0 items-center justify-center">
        {icon}
      </span>
    )}
    <span className="flex-1 text-sm text-foreground-body">{label}</span>
    {children}
  </div>
);

export const ExpandButton = ({
  open,
  active,
  onClick,
}: {
  open: boolean;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex size-8 items-center justify-center rounded-lg transition-colors",
      open ? "bg-control" : "hover:bg-surface-raised",
    )}
  >
    <ChevronDown
      className={cn(
        "size-5 transition-transform",
        open && "rotate-180",
        active ? "text-blue-400" : "text-foreground-muted",
      )}
    />
  </button>
);
